import * as React from 'react'
import { GripVertical, Trash2 } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { CustomField } from '@/lib/contacts/types'

interface ChoiceRow {
  id: string
  value: string
  contactCount: number
}

export function ManageChoicesDialog({
  field,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  field: CustomField
  pending: boolean
  error: Error | null
  onClose: () => void
  onSubmit: (choices: string[]) => void
}) {
  const [rows, setRows] = React.useState<ChoiceRow[]>(() => initialChoiceRows(field))
  const [draggingId, setDraggingId] = React.useState<string | null>(null)
  const choicesError = apiFieldMessages(error, 'choices')[0]

  function updateChoice(id: string, value: string) {
    setRows((currentRows) => currentRows.map((row) => (row.id === id ? { ...row, value } : row)))
  }

  function reorderChoice(targetId: string) {
    if (!draggingId || draggingId === targetId) return

    setRows((currentRows) => reorderedRows(currentRows, draggingId, targetId))
    setDraggingId(null)
  }

  function deleteChoice(id: string) {
    setRows((currentRows) => currentRows.filter((row) => row.id !== id))
  }

  function addChoice() {
    setRows((currentRows) => [
      ...currentRows,
      { id: `new-${Date.now()}`, value: '', contactCount: 0 },
    ])
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Manage choices - {field.label}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {choicesError && <Alert>{choicesError}</Alert>}
          {rows.map((row, index) => (
            <div
              key={row.id}
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:grid-cols-[auto_minmax(0,1fr)_6rem_auto]"
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => reorderChoice(row.id)}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 cursor-grab p-0"
                aria-label={`Drag choice ${row.value || index + 1}`}
                draggable
                disabled={pending}
                onDragStart={() => setDraggingId(row.id)}
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Input
                value={row.value}
                aria-label={`Choice ${index + 1}`}
                placeholder="Choice"
                disabled={pending}
                onChange={(event) => updateChoice(row.id, event.target.value)}
              />
              <span className="col-start-2 whitespace-nowrap text-caption text-muted-foreground sm:col-start-auto">
                {contactCountLabel(row.contactCount)}
              </span>
              <Button
                type="button"
                variant="quietDestructive"
                size="sm"
                className="col-start-3 row-start-1 sm:col-start-auto sm:row-start-auto"
                aria-label={`Delete ${row.value || `choice ${index + 1}`}`}
                disabled={pending || row.contactCount > 0}
                onClick={() => deleteChoice(row.id)}
              >
                <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Delete
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start border-dashed px-1.5 py-0.5 text-micro"
            onClick={addChoice}
          >
            + Add choice
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={() => onSubmit(rows.map((row) => row.value.trim()).filter(Boolean))}
          >
            Save choices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function initialChoiceRows(field: CustomField) {
  return field.choices.map((choice, index) => ({
    id: `${index}-${choice}`,
    value: choice,
    contactCount: field.choice_counts.find((count) => count.choice === choice)?.contact_count ?? 0,
  }))
}

function reorderedRows(rows: ChoiceRow[], draggingId: string, targetId: string) {
  const fromIndex = rows.findIndex((row) => row.id === draggingId)
  const toIndex = rows.findIndex((row) => row.id === targetId)
  if (fromIndex < 0 || toIndex < 0) return rows

  const nextRows = [...rows]
  const [draggedRow] = nextRows.splice(fromIndex, 1)
  nextRows.splice(toIndex, 0, draggedRow)
  return nextRows
}

function contactCountLabel(count: number) {
  return `${count} ${count === 1 ? 'contact' : 'contacts'}`
}
